import clsx from 'clsx';
import some from 'lodash/some';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { toggleSampleBasketMedia } from 'Apollo/Store/Showroom/UserView/Samples/Basket/resolvers';
import { openMediaMobileMenu } from 'Apollo/Store/Showroom/UserView/resolvers';
import { Media, ShowroomMobileMenuType, useGetShowroomSamplesBasketQuery } from 'generated/graphql';

import { useTimeout } from 'lib/Hooks';
import { useDownloadSingleMedia } from 'lib/ShowroomSingleMediaDownload';
import { useIsShowroomDowloadOptionEnabled, useIsShowroomMobileVersion, useIsShowroomBasketEnabled, useGetCurrentShowroomData } from 'lib/showroom';

import { CircularProgress } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import MoreVertIcon from '@material-ui/icons/MoreVertOutlined';
import DownloadIcon from '@material-ui/icons/SaveAlt';
import SearchIcon from '@material-ui/icons/Search';

import { ReactComponent as AddToSamplesBasketIcon } from 'icons/addToSamplesBasket.svg';
import { ReactComponent as AddToSamplesBasketIconHovered } from 'icons/addToSamplesBasketHovered.svg';
import { ReactComponent as RemoveFromSamplesBasketIcon } from 'icons/removeFromSamplesBasket.svg';

import { useStyles } from './styles';

export type ComponentProps = {
	media: Media;
	children: JSX.Element;
	zoomButtonAction?(): void;
	displayLabel?: boolean;
};

// TODO: This component should be refactored to use the new ShowroomMediaActionsLayout
const ShowroomMediaActions = (props: ComponentProps) => {
	const { media, children, displayLabel } = props;
	const { zoomButtonAction } = props;

	const showroom = useGetCurrentShowroomData();
	const basketEnabled = useIsShowroomBasketEnabled();
	const downloadEnabled = useIsShowroomDowloadOptionEnabled();

	const classes = useStyles({ displayLabel });
	const { t } = useTranslation();
	const { run: runTimeout, clear: clearTimeout } = useTimeout();
	const mobileVersion = useIsShowroomMobileVersion();
	const [qualityOptions, downloadMedia, { loading: downloadMediaLoading }] = useDownloadSingleMedia(media, showroom as any);

	const wrapperRef = React.useRef<HTMLDivElement>();
	const actionsContainerRef = React.useRef<HTMLDivElement>();

	const [tooltipProps, setTooltipProps] = React.useState<{ el: HTMLElement; text: string }>({ el: null, text: '' });
	const clearTooltip = React.useCallback(() => setTooltipProps({ el: null, text: '' }), []);

	const [menuAnchor, setMenuAnchor] = React.useState<HTMLButtonElement>();
	const closeMenu = React.useCallback(() => setMenuAnchor(null), []);
	const [displayed, setDisplayed] = React.useState(false);

	const mediaCloudinaryId = media.cloudinaryId;
	const showMobileMediaMenu = React.useCallback(() => openMediaMobileMenu(media.id, ShowroomMobileMenuType.Section), [media.id]);

	const isSamplesMedia = media.isSample;
	const { data: samplesBasketData } = useGetShowroomSamplesBasketQuery({
		variables: { showroomId: showroom.id },
		skip: !isSamplesMedia
	});
	const [samplesIconHovered, setSamplesIconHovered] = React.useState(false);
	const isSampleAdded = React.useMemo(
		() => isSamplesMedia && some(samplesBasketData?.showroomSamplesBasket, { id: media.id }),
		[samplesBasketData, media.id, isSamplesMedia]
	);

	const onWrapperMouseEnter = React.useCallback(() => {
		setDisplayed(true);
	}, []);

	const onWrapperMouseLeave = React.useCallback(() => {
		setDisplayed(false);
		clearTooltip();
	}, [clearTooltip]);

	const downloadButton = React.useMemo(() => {
		if (!downloadEnabled) return null;
		if (downloadMediaLoading) return <CircularProgress className={classes.loader} size={20} />;
		return (
			<IconButton
				id={`showroomMediaActionsSingleDownloadButton_${mediaCloudinaryId}`}
				className={classes.actionButton}
				disableRipple
				disableFocusRipple
				onClick={ev => {
					setMenuAnchor(ev.currentTarget);
				}}
				onMouseEnter={ev =>
					setTooltipProps({
						el: ev.currentTarget,
						text: t(`showroom.download.item.${media.mediaType.toLowerCase()}`)
					})
				}
				onMouseLeave={clearTooltip}
			>
				<DownloadIcon
					className={clsx(classes.actionButtonIcon, classes.actionButtonDownloadIcon, {
						[classes.actionButtonDownloadIconActive]: !!menuAnchor
					})}
				/>
			</IconButton>
		);
	}, [classes, clearTooltip, downloadEnabled, downloadMediaLoading, media.mediaType, mediaCloudinaryId, menuAnchor, t]);

	const addSampleButton = React.useMemo(() => {
		if (!basketEnabled || !isSamplesMedia) return null;
		return (
			<IconButton
				id={`showroomMediaActionsBasketButton_${mediaCloudinaryId}`}
				className={classes.actionButton}
				disableRipple
				disableFocusRipple
				onClick={ev => {
					setTooltipProps(prev => ({
						el: prev.el,
						text: isSampleAdded ? t('showroom.samples.list.add') : t('showroom.samples.list.remove')
					}));
					toggleSampleBasketMedia(showroom.tenant.uuid, showroom.id, media.id);
				}}
				onMouseEnter={ev => {
					setSamplesIconHovered(true);
					setTooltipProps({
						el: ev.currentTarget,
						text: isSampleAdded ? t('showroom.samples.list.remove') : t('showroom.samples.list.add')
					});
				}}
				onMouseLeave={() => {
					setSamplesIconHovered(false);
					clearTooltip();
				}}
			>
				{(() => {
					let samplesIconId, SamplesIcon;
					if (isSampleAdded) {
						samplesIconId = 'RemoveSampleIcon';
						SamplesIcon = RemoveFromSamplesBasketIcon;
					} else if (samplesIconHovered) {
						samplesIconId = 'SamplesIconHovered';
						SamplesIcon = AddToSamplesBasketIconHovered;
					} else {
						samplesIconId = 'AddSampleIcon';
						SamplesIcon = AddToSamplesBasketIcon;
					}
					return <SamplesIcon id={`${samplesIconId}_${mediaCloudinaryId}`} className={classes.actionButtonIcon} />;
				})()}
			</IconButton>
		);
	}, [basketEnabled, classes, clearTooltip, isSampleAdded, isSamplesMedia, media.id, mediaCloudinaryId, samplesIconHovered, showroom, t]);

	const actionsAvailable = !!downloadButton || !!addSampleButton;

	const mediaActions = React.useMemo(() => {
		if (!actionsAvailable) return null;
		let buttonsMenu;
		if (mobileVersion)
			buttonsMenu = (
				<IconButton
					id={`showroomMediaActionsMobileMenuButton_${mediaCloudinaryId}`}
					className={classes.actionsMobileButton}
					onClick={ev => {
						ev.stopPropagation();
						showMobileMediaMenu();
					}}
				>
					<MoreVertIcon className={classes.actionsMobileIcon} />
				</IconButton>
			);
		else {
			buttonsMenu = (
				<div className={classes.actionsMenuDesktop}>
					{downloadButton}
					{downloadButton && addSampleButton ? <Divider className={classes.actionsMenuDivider} /> : null}
					{addSampleButton}
				</div>
			);
		}

		return (
			<div className={clsx(classes.mediaActionsContainer, { [classes.mediaActionsContainerVisible]: mobileVersion || displayed })}>
				{buttonsMenu}
			</div>
		);
	}, [mobileVersion, mediaCloudinaryId, classes, showMobileMediaMenu, downloadButton, addSampleButton, displayed, actionsAvailable]);

	// Currently this button is focused on zoom action, but could be changed in a future to allow different actions besides zoom
	const zoomButton = React.useMemo(() => {
		if (!zoomButtonAction || mobileVersion) return null;
		return (
			<div className={clsx(classes.zoomButtonContainer, { [classes.zoomButtonContainerDisplayed]: displayed })}>
				<IconButton
					id={`showroomMediaActionsZoomButton_${mediaCloudinaryId}`}
					classes={{
						root: classes.zoomButton
					}}
					onClick={zoomButtonAction}
				>
					<SearchIcon className={classes.zoomButtonLabel}></SearchIcon>
				</IconButton>
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mediaCloudinaryId, zoomButtonAction, mobileVersion, displayed]);

	const singleDownloadMenu = React.useMemo(() => {
		if (mobileVersion || !downloadEnabled) return null;
		return (
			<Menu
				id={`showroomMediaActionsSingleDownloadMenu_${mediaCloudinaryId}`}
				open={!!menuAnchor}
				anchorEl={menuAnchor}
				onClose={closeMenu}
				classes={{
					paper: classes.singleDownloadMenuPaper,
					list: classes.singleDownloadMenuList
				}}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left'
				}}
			>
				{qualityOptions
					.map(({ label, value }, index) => [
						<MenuItem
							id={`showroomMediaActionsSingleDownloadOption_${value}`}
							key={`showroomMediaActionsSingleDownloadOption_${value}`}
							className={classes.singleDownloadMenuItem}
							onClick={() => {
								downloadMedia(value);
								closeMenu();
							}}
						>
							{label}
						</MenuItem>,
						index !== qualityOptions.length - 1 ? (
							<Divider
								key={`showroomMediaActionsSingleDownloadOption_divider_${value}`}
								className={classes.singleDownloadMenuDivider}
								variant="middle"
							/>
						) : null
					])
					.map(el => el)}
			</Menu>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [closeMenu, mediaCloudinaryId, menuAnchor, mobileVersion, qualityOptions, downloadMedia, downloadEnabled]);

	const tooltip = React.useMemo(
		() => (
			<Popper
				id="showroomMediaActionsTooltip"
				className={classes.tooltip}
				open={!!tooltipProps.el}
				anchorEl={tooltipProps.el}
				placement={'right'}
				disablePortal
				modifiers={{
					flip: {
						enabled: true
					}
				}}
			>
				<div id="showroomMediaActionsTooltipText">{tooltipProps.text}</div>
			</Popper>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[tooltipProps]
	);

	return (
		<div
			id={`showroomMediaActionsWrapper_${media.id}`}
			className={classes.wrapper}
			ref={wrapperRef}
			onMouseEnter={mobileVersion ? null : onWrapperMouseEnter}
			onMouseLeave={mobileVersion ? null : onWrapperMouseLeave}
			onContextMenu={ev => ev.preventDefault()}
			onTouchStart={() => runTimeout(showMobileMediaMenu, 500)}
			onTouchEnd={() => {
				clearTimeout();
			}}
		>
			<div
				id={`showroomMediaActionsContainer_${media.id}`}
				ref={actionsContainerRef}
				className={clsx(classes.mediaActionsWrapper, { [classes.mediaActionsWrapperMobile]: mobileVersion })}
			>
				<div className={classes.mediaActionsInnerWrapper}>
					{mediaActions}
					{zoomButton}
				</div>
			</div>
			{children}
			{singleDownloadMenu}
			{tooltip}
		</div>
	);
};

export default React.memo(ShowroomMediaActions);
